<!-- src/components/ui/AppButton.vue -->
<template>
  <!-- to가 있으면 RouterLink, href가 있으면 a, 아니면 button -->
  <component :is="tag" :to="to" :href="href" :type="tag === 'button' ? type : undefined" :class="classes"
    :aria-disabled="tag !== 'button' && isDisabled ? 'true' : undefined" @click="handleClick">
    <!-- 로딩이면 스피너만 -->
    <span v-if="loading" class="btn__spinner" aria-hidden="true"></span>

    <!-- 로딩이 아니면 내용 -->
    <span v-else class="btn__label">
      <slot />
    </span>
  </component>
</template>

<script setup>
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

/**
 * props는 정말 필요한 것만 넣었습니다.
 * - variant: 모양 (primary, secondary, text)
 * - size: 크기 (sm, md, lg)
 * - block: 가로 꽉 채우기
 * - loading/disabled: 상태
 * - to/href/type: 이동/폼 제출 대응
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'primary', // 'primary' | 'secondary' | 'text'
    validator: v => ['primary', 'secondary', 'text'].includes(v)
  },
  size: {
    type: String,
    default: 'md', // 'sm' | 'md' | 'lg'
    validator: s => ['sm', 'md', 'lg'].includes(s)
  },
  block: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },

  to: { type: [String, Object], default: null },
  href: { type: String, default: null },
  type: { type: String, default: 'button' }, // button | submit | reset
})

const emit = defineEmits(['click'])

const tag = computed(() => {
  if (props.to) return RouterLink
  if (props.href) return 'a'
  return 'button'
})

const isDisabled = computed(() => props.disabled || props.loading)

function handleClick(e) {
  // 비활성/로딩이면 클릭 막기
  if (isDisabled.value) {
    e.preventDefault()
    e.stopPropagation()
    return
  }
  emit('click', e)
}

const classes = computed(() => [
  'btn',
  `btn--${props.variant}`,
  `btn--${props.size}`,
  props.block && 'is-block',
  isDisabled.value && 'is-disabled',
].filter(Boolean))
</script>

<style scoped>
/* ========== 기본 ========== */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 8px;
  border: 1px solid transparent;
  padding: .6rem .9rem;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  text-decoration: none;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease, transform .8s ease;
}

.btn:active {
  transform: translateY(1px);
}

.is-block {
  width: 100%;
}

.is-disabled {
  opacity: .6;
  cursor: not-allowed;
  pointer-events: none;
}

/* ========== 크기 ========== */
.btn--sm {
  padding: .45rem .7rem;
  font-size: .875rem;
}

.btn--md {
  font-size: .95rem;
}

.btn--lg {
  padding: .8rem 1.1rem;
  font-size: 1.05rem;
  border-radius: 10px;
}

/* ========== 모양(variant) ========== */
.btn--primary {
  background: #1d2088;
  color: #fff;
}

.btn--primary:hover {
  background: #161b6f;
}

.btn--secondary {
  background: #eef2f7;
  color: #111;
  border-color: #d8e1ea;
}

.btn--secondary:hover {
  background: #e5ecf5;
}

.btn--text {
  background: transparent;
  color: #1d2088;
  border: 0;
  padding: 0;
}

/* .btn--text:hover {
  text-decoration: underline;
} */

/* ========== 스피너(순수 CSS) ========== */
.btn__spinner {
  width: 1em;
  height: 1em;
  border: 2px solid rgba(0, 0, 0, .15);
  border-top-color: currentColor;
  border-radius: 50%;
  display: inline-block;
  animation: spin .8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
